// Client Requirements section of the public share view: the client's
// answered requirements, rendered verbatim from the frozen snapshot
// (verdict, prose, key figures, quantities table, basis notes, evidence
// chips and imagery with a lightbox). View-only; no tenant access.
import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import type { ShareRequirement } from "../worker/modules/share-types";
import { SvMeta } from "./ui";

type ReqImage = ShareRequirement["answer"]["images"][number];

const VERDICT: Record<string, { label: string; color: string; bg: string; bc: string }> = {
  action: { label: "Action required", color: "#F97316", bg: "rgba(249,115,22,0.12)", bc: "rgba(249,115,22,0.32)" },
  monitor: { label: "Monitor", color: "#F5B544", bg: "rgba(245,181,68,0.12)", bc: "rgba(245,181,68,0.30)" },
  ok: { label: "No action needed", color: "#4ED08A", bg: "rgba(78,208,138,0.10)", bc: "rgba(78,208,138,0.28)" },
};

const fmtDate = (ts: number) => new Date(ts).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

function VerdictPill({ verdict }: { verdict: string | null }) {
  const m = verdict ? VERDICT[verdict] : undefined;
  if (!m) return null;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "var(--font-mono)", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: m.color, background: m.bg, border: `1px solid ${m.bc}`, borderRadius: 6, padding: "3px 9px", whiteSpace: "nowrap" }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: m.color }}></span>{m.label}
    </span>
  );
}

function Lightbox({ img, onClose }: { img: ReqImage; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);
  return (
    <div onMouseDown={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.78)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1300, padding: 32, cursor: "zoom-out" }}>
      <div className="pop-in" style={{ maxWidth: "86vw", maxHeight: "86vh", display: "flex", flexDirection: "column", gap: 10 }} onMouseDown={(e) => e.stopPropagation()}>
        <img src={img.src} alt={img.caption || ""} style={{ maxWidth: "86vw", maxHeight: "78vh", borderRadius: 12, border: "1px solid var(--di-border)", objectFit: "contain" }} />
        <SvMeta color="var(--di-muted)" style={{ letterSpacing: "0.08em" }}>{img.caption || "Answer imagery"}</SvMeta>
      </div>
    </div>
  );
}

function AnswerBody({ answer }: { answer: ShareRequirement["answer"] }) {
  const [light, setLight] = useState<ReqImage | null>(null);
  const paras = (answer.text || "").split(/\n{2,}|\n/).map((p) => p.trim()).filter(Boolean);
  const figures = answer.figures ?? [];
  const table = answer.table;
  const basis = answer.basis;
  const allImages = answer.images ?? [];
  const plates = allImages.filter((im) => im.wide);
  const thumbs = allImages.filter((im) => !im.wide);
  const cell = (i: number): CSSProperties => ({ padding: "8px 12px", textAlign: i === 0 ? "left" : "right", minWidth: 0 });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <VerdictPill verdict={answer.verdict} />
        <SvMeta color="var(--di-faint)" style={{ letterSpacing: "0.08em" }}>Answered by {answer.by}{answer.at ? " · " + fmtDate(answer.at) : ""}</SvMeta>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        {paras.map((p, i) => <p key={i} style={{ fontSize: 13, color: "var(--di-muted-2)", lineHeight: 1.66, maxWidth: 720, margin: 0 }}>{p}</p>)}
      </div>
      {figures.length > 0 && (
        <div style={{ display: "flex", gap: 9, flexWrap: "wrap" }}>
          {figures.map((f, i) => (
            <div key={i} style={{ minWidth: 138, padding: "11px 14px 12px", borderRadius: 10, background: "var(--di-ink)", border: "1px solid var(--di-border)", display: "flex", flexDirection: "column", gap: 5 }}>
              <SvMeta color="var(--di-label)" style={{ fontSize: 8.5, letterSpacing: "0.13em" }}>{f.label}</SvMeta>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 17, fontWeight: 700, color: "var(--di-text)", fontVariantNumeric: "tabular-nums", letterSpacing: "-0.01em" }}>{f.value}</span>
              {f.sub && <span style={{ fontSize: 11, color: "var(--di-faint)", lineHeight: 1.45 }}>{f.sub}</span>}
            </div>
          ))}
        </div>
      )}
      {table && table.cols.length > 0 && table.rows.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
          {table.caption && <SvMeta color="var(--di-label)">{table.caption}</SvMeta>}
          <div style={{ border: "1px solid var(--di-border)", borderRadius: 10, overflow: "hidden" }}>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(140px, 1.6fr) " + table.cols.slice(1).map(() => "minmax(74px, 1fr)").join(" "), background: "var(--di-dark)", borderBottom: "1px solid var(--di-border)" }}>
              {table.cols.map((c, i) => (
                <div key={i} style={cell(i)}>
                  <SvMeta color="var(--di-label)" style={{ fontSize: 8.5, letterSpacing: "0.13em" }}>{c}</SvMeta>
                </div>
              ))}
            </div>
            {table.rows.map((r, ri) => (
              <div key={ri} style={{ display: "grid", gridTemplateColumns: "minmax(140px, 1.6fr) " + table.cols.slice(1).map(() => "minmax(74px, 1fr)").join(" "), borderTop: ri ? "1px solid var(--di-border)" : "none" }}>
                {table.cols.map((_, i) => (
                  <div key={i} style={{ ...cell(i), fontFamily: i === 0 ? "var(--font-sans)" : "var(--font-mono)", fontSize: i === 0 ? 12.5 : 12, fontWeight: i === 0 ? 500 : 600, color: i === 0 ? "var(--di-text)" : "var(--di-muted-2)", fontVariantNumeric: "tabular-nums" }}>
                    {r[i] == null || r[i] === "" ? "—" : r[i]}
                  </div>
                ))}
              </div>
            ))}
          </div>
          {table.note && <span style={{ fontSize: 11.5, color: "var(--di-faint)", lineHeight: 1.5 }}>{table.note}</span>}
        </div>
      )}
      {basis && (basis.items.length > 0 || basis.source) && (
        <div style={{ display: "flex", flexDirection: "column", gap: 9, padding: "13px 15px 14px", borderRadius: 10, border: "1px solid var(--di-border)", background: "var(--di-navy)" }}>
          <SvMeta color="var(--di-label)">{basis.title || "Basis of measurement"}</SvMeta>
          {basis.items.length > 0 && (
            <ul style={{ display: "flex", flexDirection: "column", gap: 5, margin: 0, paddingLeft: 16 }}>
              {basis.items.map((it, i) => <li key={i} style={{ fontSize: 12.5, color: "var(--di-muted)", lineHeight: 1.55 }}>{it}</li>)}
            </ul>
          )}
          {basis.source && <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--di-faint)", wordBreak: "break-all", lineHeight: 1.5 }}>{basis.source}</span>}
        </div>
      )}
      {plates.map((img, i) => (
        <button key={img.id || "plate" + i} onClick={() => setLight(img)} title={img.caption}
          style={{ display: "block", width: "100%", padding: 0, textAlign: "left", border: "1px solid var(--di-border)", borderRadius: 12, background: "var(--di-ink)", overflow: "hidden", cursor: "zoom-in" }}>
          <img src={img.src} alt={img.caption || ""} style={{ display: "block", width: "100%", maxHeight: 340, objectFit: "cover" }} />
          <span style={{ display: "block", fontFamily: "var(--font-mono)", fontSize: 8.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--di-faint)", padding: "8px 12px 9px", borderTop: "1px solid var(--di-border)" }}>{img.caption}</span>
        </button>
      ))}
      {(answer.evidence ?? []).length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <SvMeta color="var(--di-label)">Evidence</SvMeta>
          <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
            {answer.evidence.map((ev, i) => (
              <span key={i} style={{ display: "inline-flex", alignItems: "center", fontFamily: "var(--font-mono)", fontSize: 9, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "var(--di-muted-2)", background: "var(--di-ink)", border: "1px solid var(--di-border)", borderRadius: 6, padding: "4px 9px", whiteSpace: "nowrap" }}>{ev.label}</span>
            ))}
          </div>
        </div>
      )}
      {thumbs.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <SvMeta color="var(--di-label)">Imagery</SvMeta>
          <div style={{ display: "flex", gap: 9, flexWrap: "wrap" }}>
            {thumbs.map((img, i) => (
              <button key={img.id || i} onClick={() => setLight(img)} title={img.caption}
                style={{ width: 118, borderRadius: 10, border: "1px solid var(--di-border)", background: "var(--di-ink)", overflow: "hidden", cursor: "zoom-in", padding: 0, textAlign: "left" }}>
                <img src={img.src} alt={img.caption || ""} style={{ width: "100%", height: 74, objectFit: "cover", display: "block" }} />
                <span style={{ display: "block", fontFamily: "var(--font-mono)", fontSize: 8, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--di-faint)", padding: "5px 8px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{img.caption}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {light && <Lightbox img={light} onClose={() => setLight(null)} />}
    </div>
  );
}

export function RequirementsSection({ items }: { items: ShareRequirement[] }): ReactNode {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {items.map((q) => (
        <div key={q.id} style={{ border: "1px solid var(--di-border)", borderRadius: 14, background: "var(--di-surface)", overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 13, padding: "15px 18px", borderBottom: "1px solid var(--di-border)" }}>
            <div style={{ width: 44, height: 26, borderRadius: 7, background: "var(--di-ink)", border: "1px solid var(--di-border)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontFamily: "var(--font-mono)", fontSize: 9.5, fontWeight: 700, color: "var(--di-muted)", marginTop: 1 }}>{q.ref}</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 15.5, fontWeight: 600, color: "var(--di-text)", letterSpacing: "-0.01em", lineHeight: 1.4 }}>{q.question}</div>
              <SvMeta color="var(--di-faint)" style={{ fontSize: 8.5, letterSpacing: "0.08em", display: "block", marginTop: 5 }}>Asked by {q.askedBy || "you"}{q.context ? " · " + q.context : ""}</SvMeta>
            </div>
          </div>
          <div style={{ padding: "15px 18px 17px" }}>
            <AnswerBody answer={q.answer} />
          </div>
        </div>
      ))}
    </div>
  );
}
